import { captureMessage } from '@sentry/vue';
// To be used as a backup if the dynamic import of the locale file fails.
// In that scenario, we implicitly default to English.
import hotpot from '@/localization/hotpot.json';

interface NavigatorLanguage extends Navigator {
  userLanguage?: string;
  browserLanguage?: string;
}

const DEFAULT_LANGUAGE: string = 'en';
const DEFAULT_LOCALE: string = 'en_GB';

// This must be updated if we add support for more locales
const TRANSLATED_LOCALES: Set<string> = new Set([
  'ar_AA',
  'bn_BD',
  'bn_IN',
  'da_DK',
  'de_DE',
  'el_GR',
  'en_GB',
  'es_001',
  'es_AR',
  'es_ES',
  'es_MX',
  'fi_FI',
  'fil_PH',
  'fr_FR',
  'gu_IN',
  'hi_IN',
  'id_ID',
  'it_IT',
  'ja_JP',
  'ko_KR',
  'kn_IN',
  'ml_IN',
  'mr_IN',
  'ms_MY',
  'nb_NO',
  'nl_NL',
  'pa_IN',
  'pl_PL',
  'pt_BR',
  'pt_PT',
  'ro_RO',
  'ru_RU',
  'sv_SE',
  'ta_IN',
  'te_IN',
  'th_TH',
  'tr_TR',
  'ur_PK',
  'vi_VN',
  'zh_CN',
  'zh_TW',
]);

const LANGUAGE_NORMALIZE: Record<string, string> = {
  tl: 'fil', // Filipino is 'tl' instead of 'fil' on iOS https://jira.sc-corp.net/browse/BM-6012
};

// https://stackoverflow.com/a/52112155/3712254
const getNavigatorLanguage = (): string => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  } else {
    const language =
      (<NavigatorLanguage>navigator).userLanguage ||
      navigator.language ||
      (<NavigatorLanguage>navigator).browserLanguage;
    if (!language) {
      captureMessage(`Could not find navigator language - default to ${DEFAULT_LANGUAGE}`);
      return DEFAULT_LANGUAGE;
    }
    return language;
  }
};

const getLocale = (): string => {
  const locale = getNavigatorLanguage().replace('-', '_');
  if (TRANSLATED_LOCALES.has(locale)) {
    return locale;
  }
  captureMessage(`Unable to find an exact match for ${locale}`);
  const language = locale.split('_')[0];
  for (const translatedLocale of TRANSLATED_LOCALES) {
    if (
      translatedLocale.startsWith(language) ||
      translatedLocale.startsWith(LANGUAGE_NORMALIZE[language])
    ) {
      return translatedLocale;
    }
  }
  captureMessage(`Unable to find any matching locale for ${locale}`);
  return DEFAULT_LOCALE;
};

let localization: Record<string, string>;
// Lazily loads the localization file based on the locale.
// If the loading fails, fallsback to the statically loaded english localization.
let localizationPromise: Promise<Record<string, string>> = ((): Promise<Record<string, string>> => {
  const locale = getLocale();
  console.log('Locale: ' + locale);
  if (locale === DEFAULT_LOCALE) {
    // The en_GB localization and hotpot are not identical but the differences
    // are negligible. Instead of making a new network request, load the already
    // loaded strings.
    return new Promise((resolve) => {
      localization = hotpot;
      resolve(localization);
    });
  }
  const localeFilePostFix = `localization/strings/${locale}/strings_${locale}`;
  // The "@" syntax does not work outside of import(...)
  // See https://webpack.js.org/api/module-methods/
  return import(
    /* webpackInclude: /\.json$/ */
    /* webpackChunkName: "localization" */
    /* webpackMode: "lazy" */
    `@/${localeFilePostFix}`
  )
    .then((data) => {
      localization = data;
      return localization;
    })
    .catch((e) => {
      captureMessage(`Fetching ${localeFilePostFix} failed - using backup`, {
        extra: { error: e },
      });
      localization = hotpot;
      return localization;
    });
})();

const getLocalizedText = (textKey: string): string => {
  const localizedText = localization[textKey];
  if (!localizedText) {
    captureMessage(`${textKey} is not localized`, {
      extra: { localized_texts: localization },
    });
    // TODO(berkay): Remove the first term after all the strings are translated
    return hotpot[textKey] || textKey;
  }
  return localizedText;
};

export default function(textKey: string): Promise<string> {
  if (localization) {
    // The localization is ready
    return new Promise((resolve) => {
      resolve(getLocalizedText(textKey));
    });
  } else {
    // Get the text after the localization is ready
    return localizationPromise.then(() => {
      return getLocalizedText(textKey);
    });
  }
}
